import React from 'react';
import { isEmpty } from 'lodash';

import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import EmptyState from '~/components/core/EmptyState';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import type { MoiVendorAssignmentRuleInfo, OrganizationContactRolesDict } from '~/server_shared/openapi/data-contracts';
import { subOrgIdToNameDict } from '~/Utils';

import { TEXTS } from '../texts';

import MoiAssignmentRuleRow from './MoiAssignmentRuleRow';
import MoiAssignmentRulesTableHeader from './MoiAssignmentRulesTableHeader';

/** A static contact shows its name, or its id while that name is unresolved (still loading, or the
 *  lookup failed). A role shows the organization's description of it, or the stored key when the
 *  organization no longer lists the role. */
const assigneeLabelFor = (
  assignee: MoiVendorAssignmentRuleInfo['assignee'],
  contactNamesById: Record<number, string>,
  organizationContactRolesDict: Partial<OrganizationContactRolesDict>
): string =>
  assignee.strategy === 'static'
    ? (contactNamesById[assignee.contact_id] ?? TEXTS.CONTACT_FALLBACK(assignee.contact_id))
    : TEXTS.ROLE_CELL(organizationContactRolesDict[assignee.contact_role]?.desc ?? assignee.contact_role);

interface MoiAssignmentRulesTableProps {
  organization: SysconfigOrganization;
  /** Every live rule, in priority order, unfiltered: `onReorder` reports positions in this list and
   *  the reorder endpoint takes the whole order, so a filtered subset would have no valid body. */
  rules: MoiVendorAssignmentRuleInfo[];
  /** A write is in flight: dragging and every row control are inert. */
  isProcessing: boolean;
  emptyStateText: string;
  moiMethodNameByKey: Record<string, string>;
  contactNamesById: Record<number, string>;
  onReorder: (reorder: DndOrderedListReorder) => void;
  onEditRule: (rule: MoiVendorAssignmentRuleInfo) => void;
  onDeleteRule: (rule: MoiVendorAssignmentRuleInfo) => void | Promise<void>;
  onEnabledChanged: (rule: MoiVendorAssignmentRuleInfo) => void | Promise<void>;
}

const MoiAssignmentRulesTable: React.FC<MoiAssignmentRulesTableProps> = ({
  organization,
  rules,
  isProcessing,
  emptyStateText,
  moiMethodNameByKey,
  contactNamesById,
  onReorder,
  onEditRule,
  onDeleteRule,
  onEnabledChanged,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();
  // `useOrganization` is exported from a JSX file without types; cast at the boundary. `Partial`
  // because the dictionary is read by a stored role key, which the organization may no longer list.
  const { organizationContactRolesDict } = useOrganization() as {
    organizationContactRolesDict: Partial<OrganizationContactRolesDict>;
  };
  const subOrgIdToName = subOrgIdToNameDict(organization);
  const showSubOrgColumn = !!organization.sub_organizations_enabled;

  if (isEmpty(rules)) {
    return <EmptyState subtitle={emptyStateText} />;
  }

  return (
    <div>
      <MoiAssignmentRulesTableHeader showSubOrgColumn={showSubOrgColumn} />
      <DndOrderedList
        items={rules}
        getItemId={(rule) => String(rule.id)}
        disabled={isProcessing}
        showDropLine
        onReorder={onReorder}
        renderItem={(rule, state) => (
          <MoiAssignmentRuleRow
            rule={rule}
            dragHandleProps={state.dragHandleProps}
            isDragDisabled={isProcessing}
            isProcessing={isProcessing}
            showSubOrgColumn={showSubOrgColumn}
            subOrgIdToName={subOrgIdToName}
            lobConfigurationsDict={lobConfigurationsDict}
            moiMethodNameByKey={moiMethodNameByKey}
            assigneeLabel={assigneeLabelFor(rule.assignee, contactNamesById, organizationContactRolesDict)}
            onEdit={onEditRule}
            onDelete={onDeleteRule}
            onEnabledChanged={onEnabledChanged}
          />
        )}
      />
    </div>
  );
};

export default MoiAssignmentRulesTable;
